import React, { useEffect } from 'react'
import { createTheme } from '@mui/material/styles'

/**
 * The Partner Onboarding pages' look (Partner Signups, an application,
 * Agents, Signup Standards): the same tokens, fonts and icons as the
 * merchant sign-up form (rushmyfood-web screens/PartnerSignup/signupDesign.js),
 * so the admin and the form match. Used inside a ThemeProvider per page; the
 * sidebar keeps its own look.
 */

export const T = {
  red: '#C8321F',
  redHover: '#AE2A18',
  redText: '#A8291A',
  redTextStrong: '#9E2614',
  link: '#B12A18',
  linkHover: '#8E2010',
  redTint: '#FCEEEA',
  heroBg: '#FBEDE8',
  heroBorder: '#F2DCD3',
  pageBg: '#FAF7F4',
  card: '#FFFFFF',
  cardBorder: '#ECE5DD',
  divider: '#F0EAE3',
  input: '#D6CDC3',
  inputHover: '#B9AFA4',
  fill: '#F7F2ED',
  footerBg: '#FCFAF8',
  text: '#1C1917',
  text2: '#4A443E',
  muted: '#5C554E',
  hint: '#6B635B',
  placeholder: '#8A8279',
  green: '#2E6B36',
  greenTint: '#EAF3EA',
  greenBorder: '#CFE3CF',
  amberTint: '#FFF6E8',
  amberBorder: '#F3DEB8',
  amberText: '#6B4A12',
  neutralBadgeBg: '#F1EDE8',
  neutralBadgeText: '#57504A',
  infoBg: '#EEF3FB',
  infoBorder: '#D3DFF2',
  infoText: '#1E3A66',
  purpleTint: '#F1ECFB',
  purpleText: '#4C2A85',
  disabledBg: '#E9E3DD',
  disabledText: '#7A7169',
  error: '#B3261E',
  focusRing: '0 0 0 4px rgba(200,50,31,.14)',
  display: '"Bricolage Grotesque", "Figtree", system-ui, -apple-system, "Segoe UI", sans-serif',
  body: '"Figtree", system-ui, -apple-system, "Segoe UI", sans-serif',
}

const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,600;12..96,700&family=Figtree:wght@400;500;600;700&display=swap'

/** Loads Bricolage Grotesque and Figtree once. */
export function useOnboardingFonts() {
  useEffect(() => {
    if (document.querySelector(`link[href="${FONT_HREF}"]`)) return
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = FONT_HREF
    document.head.appendChild(link)
  }, [])
}

// ── Icons: inline stroke SVGs, 2px, round caps ────────────────────────────────

function Svg({ size = 20, color = 'currentColor', children, style }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" style={{ flexShrink: 0, ...style }}
    >
      {children}
    </svg>
  )
}

export const Icon = {
  Check: p => <Svg {...p}><path d="M20 6 9 17l-5-5" /></Svg>,
  X: p => <Svg {...p}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></Svg>,
  ArrowRight: p => <Svg {...p}><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></Svg>,
  ArrowLeft: p => <Svg {...p}><path d="M19 12H5" /><path d="m12 19-7-7 7-7" /></Svg>,
  ChevronDown: p => <Svg {...p}><path d="m6 9 6 6 6-6" /></Svg>,
  Info: p => <Svg {...p}><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></Svg>,
  Alert: p => <Svg {...p}><circle cx="12" cy="12" r="10" /><path d="M12 8v4" /><path d="M12 16h.01" /></Svg>,
  Lock: p => <Svg {...p}><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></Svg>,
  Download: p => <Svg {...p}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="m7 10 5 5 5-5" /><path d="M12 15V3" /></Svg>,
  File: p => <Svg {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6" /></Svg>,
  FileText: p => <Svg {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6" /><path d="M8 13h8" /><path d="M8 17h5" /></Svg>,
  Mail: p => <Svg {...p}><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></Svg>,
  Plus: p => <Svg {...p}><path d="M12 5v14" /><path d="M5 12h14" /></Svg>,
  Refresh: p => <Svg {...p}><path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" /></Svg>,
  Search: p => <Svg {...p}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></Svg>,
  Store: p => <Svg {...p}><path d="M3 9l1.5-5h15L21 9" /><path d="M4 9v11h16V9" /><path d="M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0" /><path d="M10 20v-5h4v5" /></Svg>,
  Users: p => <Svg {...p}><circle cx="9" cy="8" r="4" /><path d="M2 21c1-3.5 3.8-5.5 7-5.5s6 2 7 5.5" /><path d="M16 4.13a4 4 0 0 1 0 7.75" /><path d="M22 21c-.6-2.4-2-4.1-4-5" /></Svg>,
  Sliders: p => <Svg {...p}><path d="M4 21v-7" /><path d="M4 10V3" /><path d="M12 21v-9" /><path d="M12 8V3" /><path d="M20 21v-5" /><path d="M20 12V3" /><path d="M2 14h4" /><path d="M10 8h4" /><path d="M18 16h4" /></Svg>,
  Sparkle: p => <Svg {...p}><path d="M12 3l1.8 4.9L19 9.7l-5.2 1.8L12 16.4l-1.8-4.9L5 9.7l5.2-1.8Z" /><path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8Z" /></Svg>,
  External: p => <Svg {...p}><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></Svg>,
  Pen: p => <Svg {...p}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></Svg>,
  Circle: p => <Svg {...p}><circle cx="12" cy="12" r="9" /></Svg>,
  Copy: p => <Svg {...p}><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></Svg>,
  Send: p => <Svg {...p}><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></Svg>,
  Link: p => <Svg {...p}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></Svg>,
  Coins: p => <Svg {...p}><circle cx="8" cy="8" r="6" /><path d="M18.09 10.37A6 6 0 1 1 10.34 18" /><path d="M7 6h1v4" /><path d="m16.71 13.88.7.71-2.82 2.82" /></Svg>,
  Calendar: p => <Svg {...p}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" /></Svg>,
  Trash: p => <Svg {...p}><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /></Svg>,
  Receipt: p => <Svg {...p}><path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 1 .67V2l-1 .67L16 2l-3 2-3-2-3 2Z" /><path d="M8 8h8" /><path d="M8 12h8" /><path d="M8 16h5" /></Svg>,
}

function SelectChevron({ className }) {
  return (
    <span className={className} style={{ display: 'inline-flex', right: 14, top: 'calc(50% - 10px)', position: 'absolute', pointerEvents: 'none', color: T.muted }}>
      <Icon.ChevronDown size={20} />
    </span>
  )
}

const inputHeight = { padding: '11px 14px', fontSize: 15, lineHeight: '22px', height: '22px' }

/** The MUI theme for the Partner Onboarding pages. */
export const onboardingTheme = createTheme({
  palette: {
    primary: { main: T.red, dark: T.redHover, contrastText: '#fff' },
    error: { main: T.error },
    success: { main: T.green },
    text: { primary: T.text, secondary: T.text2 },
    background: { default: T.pageBg, paper: T.card },
    divider: T.divider,
  },
  typography: {
    fontFamily: T.body,
    fontSize: 14,
    body1: { fontSize: 15, lineHeight: 1.5 },
    body2: { fontSize: 14, lineHeight: 1.5 },
    button: { fontWeight: 600, textTransform: 'none' },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 999, textTransform: 'none', fontWeight: 600, minHeight: 40, '&.Mui-focusVisible': { boxShadow: T.focusRing } },
      },
    },
    MuiIconButton: { styleOverrides: { root: { '&.Mui-focusVisible': { boxShadow: T.focusRing } } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          background: '#fff',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: T.input, top: 0, '& legend': { display: 'none' } },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.inputHover },
          '&.Mui-focused': { boxShadow: T.focusRing },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.red, borderWidth: 1 },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: T.error },
          '&.Mui-disabled': { background: T.fill },
        },
        input: { ...inputHeight, '&::placeholder': { color: T.placeholder, opacity: 1 } },
        inputSizeSmall: inputHeight,
        multiline: { padding: 0 },
        adornedStart: { paddingLeft: 14 },
        adornedEnd: { paddingRight: 10 },
      },
    },
    MuiSelect: {
      defaultProps: { IconComponent: SelectChevron },
      styleOverrides: { select: { appearance: 'none', paddingRight: '44px !important' } },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { marginLeft: 0, marginRight: 0, marginTop: 6, fontSize: 13, lineHeight: 1.45, color: T.hint, '&.Mui-error': { color: T.error } } },
    },
    MuiCheckbox: { defaultProps: { color: 'primary' }, styleOverrides: { root: { color: T.inputHover } } },
    MuiSwitch: { defaultProps: { color: 'primary' } },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 16, alignItems: 'flex-start', fontSize: 14 },
        standardInfo: { background: T.infoBg, border: `1px solid ${T.infoBorder}`, color: T.infoText },
        standardError: { background: '#FCEEEC', border: '1px solid #F3D3CE', color: '#7A1D14' },
        standardSuccess: { background: T.greenTint, border: `1px solid ${T.greenBorder}`, color: '#1F4D25' },
        standardWarning: { background: T.amberTint, border: `1px solid ${T.amberBorder}`, color: T.amberText },
      },
    },
    MuiChip: { styleOverrides: { root: { borderRadius: 999, fontWeight: 600 } } },
    MuiPaper: { styleOverrides: { rounded: { borderRadius: 20 } } },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 20 } } },
    MuiDialogTitle: { styleOverrides: { root: { fontFamily: T.display, fontWeight: 700, letterSpacing: '-0.02em', fontSize: 22, color: T.text } } },
    MuiTableCell: { styleOverrides: { root: { borderColor: T.divider, fontSize: 14, color: T.text }, head: { color: T.muted, fontWeight: 600 } } },
    MuiMenuItem: { styleOverrides: { root: { fontSize: 14 } } },
  },
})
